<template>
  <div ref="rightSide">
    <div class="flex flex-col gap-5 lg:gap-6">
      <div class="max-lg:visually-hidden">
        <h1 class="mb-5 text-xl font-light !tracking-[0.2px] text-gray-100">
          <span v-if="isFilterClicked" data-e2e-item="product-title">
            {{ productTitle }}
          </span>
          <span v-else class="inline-block max-lg:max-w-[215px]">
            {{ $t("designYourEngagementRing") }}
          </span>
        </h1>
        <p v-if="isFilterClicked" class="text-lg font-light capitalize">
          {{ $t("customizeYourEngagementRing") }}
        </p>
        <p v-else class="text-base font-light">
          {{ $t("startCustomizingBelow") }}
        </p>
      </div>
      <div class="space-y-3.5 border-t border-off-white-30 max-lg:-mx-4">
        <UiExpandedBlock
          v-for="(filters, key, index) in groupedFiltersByTabs"
          :key="key"
          :model-value="true"
          content-classes="mt-0"
        >
          <template #activator="{ attrs, open }">
            <button
              v-bind="attrs"
              class="flex w-full items-center justify-between bg-off-white-30 p-4 text-sm uppercase text-red-80"
            >
              <span class="text-left">
                <span
                  class="mr-2.5 inline-block bg-white px-4 py-1 font-bold text-primary"
                >
                  {{ index + 1 }}
                </span>
                <span class="font-light">{{ tabsTitle[key] }}</span>
              </span>
              <UiIcon
                :name="open ? 'chevron_up' : 'chevron_down'"
                class="pointer-events-none size-6 flex-shrink-0 text-primary"
              />
            </button>
          </template>
          <template #content>
            <div class="max-lg:px-4">
              <div v-for="block in filters" :key="block?.id">
                <ProductMaterialFilter
                  :result="replacedResult"
                  :additional-options="additionalOptions"
                  :block="block"
                  label-class="uppercase font-light text-xs"
                  hide-learn-more-link
                  show-if-one-property-in-list
                  :skip-disable="!isFilterClicked"
                  :disabled-but-clickable="
                    block.sku === propertiesGroupSkus.shape ||
                    block.sku === propertiesGroupSkus.style
                  "
                  @filter="onFilterClick"
                  @options-filter="onOptionsFilterClick"
                >
                  <template #additional="{ block }">
                    <div
                      v-if="block.sku === propertiesGroupSkus.stoneSize"
                      class="ui-text-m mt-4 font-light text-gray-70"
                    >
                      {{ $t("sampleImageShownWithStone") }}
                    </div>
                  </template>
                </ProductMaterialFilter>
              </div>
            </div>
          </template>
        </UiExpandedBlock>
      </div>
    </div>

    <template v-if="!isOutOfStock">
      <div
        v-sticky="true"
        sticky-side="bottom"
        class="!inset-x-0 !w-auto border-gray-30 bg-white lg:hidden [&.bottom-sticky]:border-t"
        :class="isFilterClicked ? 'block' : 'hidden'"
      >
        <div
          class="add-to-bag-section relative items-center py-3 [.bottom-sticky_&]:container"
        >
          <div class="add-to-bag-btn">
            <UiButton
              :loading="addToCartLoading"
              class="w-full uppercase"
              size="lg"
              :disabled="addToCartLoading || hasUnavailableMaterial"
              data-e2e-btn="add-to-cart"
              @click="onAddToCart"
            >
              <span class="inline lg:hidden">
                {{ $n(finalProductPrice, "currency") }}
                <span class="mx-3 inline-block">|</span>
              </span>
              {{ $t("addToBag") }}

              <UiIcon name="bag" class="ml-2 size-6" />
            </UiButton>
          </div>
          <SharedFieldHint
            v-if="hasUnavailableMaterial"
            class="mt-4"
            :message="$t('unavailableConfiguration')"
            error
          />
        </div>
      </div>
    </template>
    <div
      v-if="currentConfiguration?.data"
      :class="isFilterClicked ? 'flex' : 'hidden'"
      class="mb-3 mt-2 justify-center bg-off-white-30 p-4 empty:hidden max-lg:-mx-4 max-lg:-mb-2 max-lg:mt-3 max-lg:flex-col lg:items-center"
    >
      <div
        v-if="isOutOfStock"
        class="mr-5 flex items-center gap-2.5 pb-2.5 text-xs font-light uppercase lg:py-1.5"
      >
        {{ $t("outOfStock") }}
      </div>
      <div v-if="!isOutOfStock && !lgAndUp" class="w-full">
        <ProductAffirmBlock />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  FilteredMaterialsByCategoriesI,
  IProductMaterialFilterEvent,
  IProductPdpInfoProps,
} from "ecom-types";

import ProductMaterialFilter from "~/components/product/ProductMaterialFilter.vue";
import { propertiesGroupSkus, propertiesGroupSlugs } from "~/configs";
import {
  defaultConfigForDesignAndShape,
  defaultConfigForStyleShape,
} from "~/configs/engagementRingConfig";

const HIDDEN_MATERIALS_FOR_CATEGORIES = {
  [propertiesGroupSkus.shape]: ["TL"],
  [propertiesGroupSkus.stoneSize]: ["2.25CT"],
};

const props = defineProps<
  IProductPdpInfoProps & { isFilterClicked?: boolean }
>();

const emit = defineEmits([
  "addToCart",
  "optionsFilter",
  "filter",
  "updateWishlist",
  "reinit-affirm",
  "replaceCurrentSku",
]);

const { t } = useI18n();

const { lgAndUp } = useAppBreakpoints();

const tabs = {
  shape: [propertiesGroupSkus.shape, propertiesGroupSkus.stoneSize],
  settings: [
    propertiesGroupSkus.style,
    propertiesGroupSkus.designCategory,
    propertiesGroupSkus.metalColor,
    propertiesGroupSkus.metalCaratWeight,
    propertiesGroupSkus.ringSize,
  ],
  stone: [propertiesGroupSkus.stoneType],
};

const propertiesGroupOverwrites = {
  [propertiesGroupSkus.shape]: {
    title: "",
    view_type: filterViewType.icon,
    sort_order: 1,
  },
  [propertiesGroupSkus.stoneSize]: {
    title: t("centerStoneCaratWeight"),
    view_type: filterViewType.button,
    sort_order: 2,
  },

  [propertiesGroupSkus.style]: {
    title: t("style"),
    view_type: filterViewType.button_big,
    sort_order: 3,
  },
  [propertiesGroupSkus.designCategory]: {
    title: t("design"),
    view_type: filterViewType.icon,
    sort_order: 4,
  },
  [propertiesGroupSkus.metalColor]: {
    title: t("chooseYourColor"),
    view_type: filterViewType.icon,
    sort_order: 5,
  },
  [propertiesGroupSkus.metalCaratWeight]: {
    title: t("metalType"),
    view_type: filterViewType.button_big,
    sort_order: 6,
  },
  [propertiesGroupSkus.ringSize]: {
    title: t("selectYourRingSize"),
    view_type: filterViewType.button,
    sort_order: 7,
  },
  [propertiesGroupSkus.stoneType]: {
    title: "",
    view_type: filterViewType.checkbox,
    sort_order: 8,
  },
};

type TabKeys = keyof typeof tabs;

watch(
  lgAndUp,
  () => {
    emit("reinit-affirm", lgAndUp);
  },
  { immediate: true },
);

const tabsTitle = computed<Record<TabKeys, string>>(() => {
  return {
    shape: t("chooseYourShape"),
    settings: t("chooseYourSetting"),
    stone: t("chooseYourStoneType"),
  };
});

const stylesResult = computed(() => {
  return (
    props.result[propertiesGroupSlugs.style] ||
    props.additionalOptions[propertiesGroupSlugs.style]
  );
});

const shapeResult = computed(() => {
  return (
    props.result[propertiesGroupSlugs.shape] ||
    props.additionalOptions[propertiesGroupSlugs.shape]
  );
});

const designResult = computed(() => {
  return (
    props.result[propertiesGroupSlugs.designCategory] ||
    props.additionalOptions[propertiesGroupSlugs.designCategory]
  );
});

const replacedResult = computed(() => {
  const resetResult = Object.fromEntries(
    Object.keys(props.result).map((key) => [key, null]),
  );
  return !props.isFilterClicked ? resetResult : props.result;
});

const overwrittenCategories = computed(() => {
  const categories = JSON.parse(
    JSON.stringify(props.filteredMaterialsByCategories),
  ) as FilteredMaterialsByCategoriesI[];
  return categories.map((category) => {
    if (category.sku === propertiesGroupSkus.shape) {
      category.materials = category.materials.map((material) => {
        if (designResult.value && stylesResult.value) {
          const designByStyle =
            defaultConfigForDesignAndShape[stylesResult.value.sku];
          const shapesByDesign = designByStyle[designResult.value.sku];

          return {
            ...material,
            disabled: !(material.sku in shapesByDesign),
          };
        } else {
          return material;
        }
      });
    }
    if (HIDDEN_MATERIALS_FOR_CATEGORIES[category.sku]) {
      category.materials = category.materials.filter(
        (material) =>
          !HIDDEN_MATERIALS_FOR_CATEGORIES[category.sku].includes(material.sku),
      );
    }
    const configurationMaterialsDisabled =
      props.currentConfiguration?.data?.materials_disabled || [];
    if (configurationMaterialsDisabled.length) {
      category.materials = category.materials.map((material) => {
        return {
          ...material,
          disabled:
            material.disabled ||
            configurationMaterialsDisabled.includes(material.id),
        };
      });
    }
    const overwrittenCategory = propertiesGroupOverwrites[category.sku];
    if (overwrittenCategory) {
      category.view_type = overwrittenCategory.view_type;
      category.sort_order =
        overwrittenCategory.sort_order || category.sort_order;
      category.currentTranslation.title = overwrittenCategory.title;

      if (category.sku === propertiesGroupSkus.designCategory) {
        if (stylesResult.value && stylesResult.value.tags?.length) {
          category.materials = category.materials.filter((material) =>
            stylesResult.value?.tags?.some((tag) => {
              return tag.currentTranslation.name === material.sku;
            }),
          );
        }
        if (shapeResult.value && shapeResult.value.tags?.length) {
          category.materials = category.materials.filter((material) => {
            return shapeResult.value?.tags?.some((tag) => {
              return tag.currentTranslation.name === material.sku;
            });
          });
        }
      }
    }
    return category;
  });
});

const groupedFiltersByTabs = computed(() => {
  const result = overwrittenCategories.value.reduce(
    (acc, category) => {
      Object.keys(tabs).forEach((tab) => {
        if (tabs[tab].includes(category.sku)) {
          acc[tab].push(category);
        }
      });
      return acc;
    },
    {
      shape: [],
      settings: [],
      stone: [],
    } as Record<TabKeys, FilteredMaterialsByCategoriesI[]>,
  );

  // sort items within each category
  result.shape.sort((a, b) => a.sort_order - b.sort_order);
  result.settings.sort((a, b) => a.sort_order - b.sort_order);
  result.stone.sort((a, b) => a.sort_order - b.sort_order);

  return result;
});

function checkAvailabilityShape(
  value: IProductMaterialFilterEvent & { item?: { disabled?: boolean } },
) {
  if (value?.item?.disabled && stylesResult.value) {
    const DEFAULT_STYLE = "SLTR";
    const DEFAULT_DESIGN = "SO";
    return (
      defaultConfigForDesignAndShape[DEFAULT_STYLE][DEFAULT_DESIGN][
        value.item.sku
      ] || ""
    );
  } else {
    return "";
  }
}

function onAddToCart() {
  if (props.hasUnavailableMaterial) return;
  emit("addToCart");
}

function onOptionsFilterClick(value: IProductMaterialFilterEvent) {
  emit("optionsFilter", value);
}

function onFilterClick(value: IProductMaterialFilterEvent) {
  if (value.slug === propertiesGroupSlugs.shape) {
    const newValue = checkAvailabilityShape(value);
    if (newValue) {
      emit("replaceCurrentSku", newValue);
    } else {
      emit("filter", value);
    }
  } else {
    emit("filter", value);
  }
}
</script>
